import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarDay,
    faCalendarXmark,
    faCircleExclamation,
    faPaperPlane,
    faRightLeft,
    faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import Section from "./Section";
import ImpactRow from "./ImpactRow";
import MissingQualifications from "./MissingQualifications";
import ShiftActionPicker from "./ShiftActionPicker";
import ShiftDetails from "./ShiftDetails";
import { smallText, tones } from "./constants";

const requestIcons = {
    "open-shift": faCalendarDay,
    "direct-offer": faPaperPlane,
    "swap-shifts": faRightLeft,
};

const ImpactList = ({
    message,
    impact,
    shiftActions,
    onShiftActionsChange,
    permissions,
    translationPrefix = "qualification-impact",
    continueLabel = "qualification-change-continue",
}) => {
    const { t } = useTranslation();
    const requests = impact?.items ?? [];
    const shifts = impact?.shifts?.items ?? [];

    if (!requests.length && !shifts.length) {
        return <span>{message}</span>;
    }

    const canChangeShifts = permissions.canMove || permissions.canDelete;

    const setAllShiftActions = (action) =>
        onShiftActionsChange(Object.fromEntries(shifts.map((shift) => [shift.shiftId, action])));

    const allShiftsAction = shifts.every((shift) => shiftActions[shift.shiftId] === shiftActions[shifts[0]?.shiftId])
        ? shiftActions[shifts[0]?.shiftId]
        : undefined;

    return (
        <div className="w-100 text-left">
            <div className="pr-1" style={{ maxHeight: 320, overflowY: "auto" }}>
                {requests.length > 0 && (
                    <Section
                        icon={faCalendarXmark}
                        tone="danger"
                        count={impact?.total ?? requests.length}
                        title={t(`${translationPrefix}-requests-title`)}
                        description={t(`${translationPrefix}-requests-description`)}
                    >
                        {requests.map((item) => (
                            <ImpactRow
                                key={item.id}
                                icon={requestIcons[item.type] ?? faCalendarDay}
                                title={t(item.type)}
                                startDate={item.startDate}
                                endDate={item.endDate}
                            >
                                <span style={{ ...smallText, color: tones.danger.color }}>
                                    {t(`${translationPrefix}-${item.effect}`)}
                                </span>
                            </ImpactRow>
                        ))}
                    </Section>
                )}

                {shifts.length > 0 && (
                    <Section
                        icon={faUserCheck}
                        tone="warning"
                        count={impact?.shifts?.total ?? shifts.length}
                        title={t(`${translationPrefix}-shifts-title`)}
                        description={t(`${translationPrefix}-shifts-description`)}
                        action={!canChangeShifts ? (
                            <div className="d-flex align-items-center rounded px-3 py-2 mb-2" style={{ ...smallText, ...tones.danger }}>
                                <FontAwesomeIcon icon={faCircleExclamation} className="mr-2" />
                                {t(`${translationPrefix}-no-shift-permission`)}
                            </div>
                        ) : shifts.length > 1 && (
                            <div className="d-flex align-items-center justify-content-end mb-2">
                                <span className="text-muted mr-2" style={smallText}>{t("apply-to-all")}</span>
                                <ShiftActionPicker
                                    value={allShiftsAction}
                                    onChange={setAllShiftActions}
                                    permissions={permissions}
                                />
                            </div>
                        )}
                    >
                        {shifts.map((shift) => (
                            <ImpactRow
                                key={shift.shiftId}
                                icon={faCalendarDay}
                                title={t("shift")}
                                startDate={shift.startDate}
                                endDate={shift.endDate}
                            >
                                <ShiftDetails shift={shift} />
                                <MissingQualifications shiftId={shift.shiftId} missing={shift.missing} />
                                {canChangeShifts && (
                                    <div className="pt-1">
                                        <ShiftActionPicker
                                            value={shiftActions[shift.shiftId]}
                                            onChange={(action) => onShiftActionsChange({ ...shiftActions, [shift.shiftId]: action })}
                                            permissions={permissions}
                                        />
                                    </div>
                                )}
                            </ImpactRow>
                        ))}
                    </Section>
                )}
            </div>

            {(canChangeShifts || !shifts.length) && (
                <div className="border-top pt-3 font-weight-bold">
                    {t(continueLabel)}
                </div>
            )}
        </div>
    );
};

export default ImpactList;
